import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, MapPin, Globe, Phone, Mail, Building2, User, Image as ImageIcon, Star, CheckCircle } from 'lucide-react';
import PublicNav from './components/PublicNav';
import Footer from './components/Footer';
import DesignerAvailabilityBadge from './components/DesignerAvailabilityBadge';

const JOHN_CONNORS = {
  name: 'John Connors',
  business: 'AspenRidge Garden Design',
  location: 'Victoria, BC',
  experience: '30 years',
  specialties: ['Landscape Design', 'Project Management', 'Consultation'],
  bio: 'Creative design and practical construction experience. John tailors every project to fit you and your family.',
  website: 'https://aspenridgegardendesign.com',
  initials: 'JC',
  videoUrl: 'https://www.youtube.com/embed/OFy2evDZLZ0',
};

const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

/** Pass through Get Ideas prefill from sessionStorage when user was sent here to pick a designer. */
function sendBriefLinkTo(designerId) {
  try {
    const raw = sessionStorage.getItem('yardBriefPrefill');
    if (!raw) return `/send-brief/${designerId}`;
    const pre = JSON.parse(raw);
    const qid = pre?.questionId || '';
    const search = qid ? `?questionId=${encodeURIComponent(qid)}` : '';
    return {
      pathname: `/send-brief/${designerId}`,
      search,
      state: { yardBriefPrefill: pre },
    };
  } catch {
    return `/send-brief/${designerId}`;
  }
}

export default function FindDesigner() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [designers, setDesigners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [selectedDesigner, setSelectedDesigner] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [highlightedDesignerId, setHighlightedDesignerId] = useState(null);

  // Auth check for Design & Collaborate button
  const currentUser = JSON.parse(localStorage.getItem('userData') || localStorage.getItem('user') || '{}');
  const currentUserId = currentUser?.id;

  useEffect(() => {
    loadDesigners();
    
    // Check for designer ID in URL params
    const designerId = searchParams.get('designer');
    if (designerId) {
      setHighlightedDesignerId(designerId);
      // Scroll to designer after load
      setTimeout(() => {
        const designerCard = document.querySelector(`[data-designer-id="${designerId}"]`);
        if (designerCard) {
          designerCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
          designerCard.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2', 'rounded-lg');
          setTimeout(() => {
            designerCard.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2');
            setHighlightedDesignerId(null);
          }, 3000);
        }
      }, 1000);
    }
  }, [cityFilter, searchTerm, searchParams]);

  const loadDesigners = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (cityFilter) params.append('city', cityFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await fetch(`${API_BASE_URL}/designers/public?${params.toString()}`);
      const data = await response.json();
      
      if (data.success) {
        setDesigners(data.designers || []);
      } else {
        console.error('Failed to load designers:', data.error);
      }
    } catch (error) {
      console.error('Error loading designers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDesignerClick = (designer) => {
    setSelectedDesigner(designer);
    setShowDetailModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedDesigner(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <PublicNav currentUserId={currentUserId} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 w-full flex-1">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Find a Designer</h1>
          <p className="mt-2 text-gray-600">Connect with professional landscape designers and contractors</p>
        </div>

        {/* ── FEATURED DESIGNER: JOHN CONNORS ── */}
        <div className="mb-8 rounded-2xl overflow-hidden border border-green-200 shadow-sm bg-white">
          <div className="px-4 py-2 text-xs font-semibold uppercase tracking-widest bg-green-600 text-white">
            Featured designer
          </div>
          <div className="grid md:grid-cols-2 gap-0">
            {/* Left: profile info */}
            <div className="p-6 flex flex-col">
              <div className="flex items-center gap-4 mb-4">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center font-bold text-xl text-white flex-shrink-0"
                  style={{ background: '#16a34a' }}
                >
                  {JOHN_CONNORS.initials}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{JOHN_CONNORS.name}</h2>
                  <p className="text-sm text-gray-600">{JOHN_CONNORS.business}</p>
                  <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                    <MapPin className="w-3.5 h-3.5" />
                    {JOHN_CONNORS.location} · {JOHN_CONNORS.experience} experience
                  </div>
                  <div className="mt-2">
                    <DesignerAvailabilityBadge acceptingNewProjects />
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">{JOHN_CONNORS.bio}</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {JOHN_CONNORS.specialties.map((s) => (
                  <span key={s} className="text-xs px-2.5 py-0.5 rounded-full bg-green-100 text-green-800 font-medium">
                    {s}
                  </span>
                ))}
              </div>
              <div className="mt-auto flex flex-wrap items-center gap-3">
                <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5">
                  Initial consultation from $99
                </span>
                <a
                  href={JOHN_CONNORS.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-semibold text-green-700 hover:underline flex items-center gap-1"
                >
                  <Globe className="w-3.5 h-3.5" />
                  aspenridgegardendesign.com
                </a>
              </div>
            </div>
            {/* Right: video embed */}
            <div className="relative bg-gray-900" style={{ minHeight: '240px' }}>
              <iframe
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                src={JOHN_CONNORS.videoUrl}
                title="John Connors – Initial Consult, Victoria BC"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>

      {/* Search and Filters */}
      <div>
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name or business..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Filter by city..."
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            <p className="mt-4 text-gray-600">Loading designers...</p>
          </div>
        ) : designers.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm">
            <p className="text-gray-600">No designers found matching your criteria.</p>
            <p className="text-sm text-gray-500 mt-2">Try adjusting your search or filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {designers.map((designer) => (
              <div
                key={designer.id}
                data-designer-id={designer.id}
                onClick={() => handleDesignerClick(designer)}
                className={`bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden ${
                  highlightedDesignerId === designer.id ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                }`}
              >
                {/* Profile Photo */}
                <div className="h-48 bg-gray-200 flex items-center justify-center">
                  {designer.profilePhoto ? (
                    <img
                      src={designer.profilePhoto}
                      alt={designer.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-16 h-16 text-gray-400" />
                  )}
                </div>

                {/* Info */}
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">
                    {designer.businessName || designer.name}
                  </h3>
                  {designer.businessName && (
                    <p className="text-sm text-gray-600 mb-2">{designer.name}</p>
                  )}
                  <div className="mb-3">
                    <DesignerAvailabilityBadge acceptingNewProjects={designer.acceptingNewProjects} />
                  </div>

                  <div className="space-y-2 text-sm text-gray-600">
                    {designer.city && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>
                          {designer.city}
                          {designer.provinceState && `, ${designer.provinceState}`}
                          {designer.country ? `, ${designer.country}` : ''}
                        </span>
                      </div>
                    )}
                    {designer.website && (
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        <a
                          href={designer.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-green-600 hover:text-green-700 truncate"
                        >
                          Visit Website
                        </a>
                      </div>
                    )}
                  </div>

                  {designer.bio && (
                    <p className="mt-4 text-sm text-gray-700 line-clamp-3">{designer.bio}</p>
                  )}

                  {designer.specialties && designer.specialties.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {designer.specialties.slice(0, 3).map((specialty, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
                        >
                          {typeof specialty === 'string' ? specialty : specialty.name || specialty}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <Link
                      to={sendBriefLinkTo(designer.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="text-sm font-semibold text-green-700 hover:underline"
                    >
                      Send a yard brief
                    </Link>
                  </div>

                  {/* Endorsements */}
                  {designer.endorsedBy && designer.endorsedBy.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        <span className="text-xs font-semibold text-gray-700">Endorsed by</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {designer.endorsedBy.slice(0, 2).map((endorsement, idx) => (
                          <div key={idx} className="flex items-center gap-1">
                            {endorsement.endorser_photo ? (
                              <img
                                src={endorsement.endorser_photo}
                                alt={endorsement.endorser_name}
                                className="w-5 h-5 rounded-full"
                              />
                            ) : (
                              <User className="w-5 h-5 text-gray-400" />
                            )}
                            <span className="text-xs text-gray-600">
                              {endorsement.endorser_business || endorsement.endorser_name}
                            </span>
                          </div>
                        ))}
                        {designer.endorsedBy.length > 2 && (
                          <span className="text-xs text-gray-500">
                            +{designer.endorsedBy.length - 2} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Designer Detail Modal */}
      {showDetailModal && selectedDesigner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto m-2 sm:m-0">
            <div className="p-4 sm:p-6">
              {/* Header */}
              <div className="flex justify-between items-start mb-4 sm:mb-6">
                <div className="flex-1 pr-2">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                    {selectedDesigner.businessName || selectedDesigner.name}
                  </h2>
                  {selectedDesigner.businessName && (
                    <p className="text-base sm:text-lg text-gray-600 mt-1">{selectedDesigner.name}</p>
                  )}
                  <div className="mt-2">
                    <DesignerAvailabilityBadge acceptingNewProjects={selectedDesigner.acceptingNewProjects} />
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl sm:text-3xl min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Close modal"
                >
                  ×
                </button>
              </div>

              {/* Profile Photo */}
              {selectedDesigner.profilePhoto && (
                <div className="mb-6">
                  <img
                    src={selectedDesigner.profilePhoto}
                    alt={selectedDesigner.name}
                    className="w-full h-64 object-cover rounded-lg"
                  />
                </div>
              )}

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {(selectedDesigner.streetAddress || selectedDesigner.city) && (
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Location</p>
                      <p className="text-gray-900">
                        {selectedDesigner.streetAddress && `${selectedDesigner.streetAddress}, `}
                        {selectedDesigner.city}
                        {selectedDesigner.provinceState && `, ${selectedDesigner.provinceState}`}
                        {selectedDesigner.country ? `, ${selectedDesigner.country}` : ''}
                      </p>
                    </div>
                  </div>
                )}
                {selectedDesigner.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="text-gray-900">{selectedDesigner.phone}</p>
                    </div>
                  </div>
                )}
                {selectedDesigner.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <a
                        href={`mailto:${selectedDesigner.email}`}
                        className="text-green-600 hover:text-green-700 hover:underline"
                      >
                        {selectedDesigner.email}
                      </a>
                    </div>
                  </div>
                )}
                {selectedDesigner.website && (
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Website</p>
                      <a
                        href={selectedDesigner.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 hover:text-green-700"
                      >
                        Visit Website
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* Consult pricing CTA */}
              <div className="mb-6 flex items-center gap-3 p-4 rounded-xl bg-green-50 border border-green-200">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <p className="text-sm text-green-800 font-semibold">Initial consultation from $99</p>
              </div>

              {/* Bio */}
              {selectedDesigner.bio && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">About</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedDesigner.bio}</p>
                </div>
              )}

              {/* Service Areas */}
              {selectedDesigner.serviceAreas && selectedDesigner.serviceAreas.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Service Areas</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedDesigner.serviceAreas.map((area, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                      >
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Specialties */}
              {selectedDesigner.specialties && selectedDesigner.specialties.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Specialties</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedDesigner.specialties.map((specialty, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                      >
                        {typeof specialty === 'string' ? specialty : specialty.name || specialty}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Portfolio */}
              {selectedDesigner.portfolioPhotos && selectedDesigner.portfolioPhotos.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Portfolio</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {selectedDesigner.portfolioPhotos.map((photo, idx) => (
                      <img
                        key={idx}
                        src={photo}
                        alt={`Portfolio ${idx + 1}`}
                        className="w-full h-48 object-cover rounded-lg"
                        onError={(e) => {
                          e.target.src = 'https://placehold.co/300x200/e5e7eb/6b7280?text=Image';
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-2 pt-4 border-t border-gray-200">
                <Link
                  to={sendBriefLinkTo(selectedDesigner.id)}
                  onClick={handleCloseModal}
                  className="block w-full text-center px-4 py-3 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700"
                >
                  Send a yard brief
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
      <Footer />
    </div>
  );
}

